import { buildAdvancedChartOptions } from './buildAdvancedChartOptions'
import { buildCartesianChartOptions } from './buildCartesianChartOptions'
import { buildComparativeChartOptions } from './buildComparativeChartOptions'
import { buildCorrelationChartOptions } from './buildCorrelationChartOptions'
import { buildHeatmapChartOptions } from './buildHeatmapChartOptions'
import { buildHierarchyChartOptions } from './buildHierarchyChartOptions'
import { buildPieChartOptions } from './buildPieChartOptions'
import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { resolveChartFields } from './resolveChartFields'

export type { BuildCategoricalChartOptionsInput, CategoricalChartOptions }

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Lay the backend's `default_options` beneath the builder's output: objects merge key by key and
 *  whatever the builder set wins, so backend defaults fill gaps without undoing renderer behaviour. */
function withBackendDefaults(
  defaults: Readonly<Record<string, unknown>>,
  options: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...defaults }
  for (const [key, value] of Object.entries(options)) {
    const base = merged[key]
    merged[key] =
      isPlainObject(base) && isPlainObject(value) ? withBackendDefaults(base, value) : value
  }
  return merged
}

function buildForType(input: BuildCategoricalChartOptionsInput): CategoricalChartOptions | null {
  const fields = resolveChartFields(input)
  if (!fields) return null

  if (input.type === 'pie' || input.type === 'donut') {
    return buildPieChartOptions(input, fields, input.type === 'donut')
  }
  if (input.type === 'multi_pie') return buildPieChartOptions(input, fields, false, 4)
  if (input.type === 'scatter' || input.type === 'bubble') {
    return buildCorrelationChartOptions(input, fields, input.type === 'bubble')
  }
  if (input.type === 'heatmap') return buildHeatmapChartOptions(input, fields)
  if (input.type === 'treemap' || input.type === 'sunburst') {
    return buildHierarchyChartOptions(input, fields, input.type)
  }
  if (
    input.type === 'line' ||
    input.type === 'area' ||
    input.type === 'stacked_area' ||
    input.type === 'multi_line' ||
    input.type === 'compare_column' ||
    input.type === 'compare_bar' ||
    input.type === 'column' ||
    input.type === 'bar' ||
    input.type === 'stacked_column' ||
    input.type === 'stacked_bar' ||
    input.type === 'combination'
  ) {
    return buildCartesianChartOptions(input, input.type, fields)
  }
  if (
    input.type === 'multi_metric' ||
    input.type === 'dumbbell' ||
    input.type === 'bullet' ||
    input.type === 'diverging_bar'
  ) {
    return buildComparativeChartOptions(input, fields)
  }
  return buildAdvancedChartOptions(input, fields)
}

/** Pure option builder for the exact types registered in `chartRendererRegistry`. */
export function buildCategoricalChartOptions(
  input: BuildCategoricalChartOptionsInput,
): CategoricalChartOptions | null {
  const options = buildForType(input)
  const defaults = input.presentation?.defaultOptions
  if (!options || !defaults || Object.keys(defaults).length === 0) return options
  return withBackendDefaults(
    defaults,
    options as Record<string, unknown>,
  ) as CategoricalChartOptions
}
